/**
 * How a failure is shown to a person.
 *
 * Every non-2xx response from halyard-monitor-api is an RFC 9457 problem
 * document — `type`, `title`, `status`, optional `detail`, `request_id` — and
 * consumer obligations §5 is explicit about what this UI may do with it:
 * surface `title`, optionally `detail`, show `request_id` so it can be quoted
 * to support, and **reconstruct nothing the API withheld**.
 *
 * So this component renders the document and does not paraphrase it. Two rules
 * it exists to enforce:
 *
 *  - **404 stays 404.** `uiSafeMessage` returns "Not found." and nothing more.
 *    There is no branch here that turns it into "Forbidden", "Access denied" or
 *    "you may not have permission", because the API answers 404 byte-identically
 *    for a resource that does not exist and one belonging to another
 *    organisation (REQ-703, §10 rule 8). Softening it in the front end hands
 *    back the enumeration oracle the API went to trouble to withhold.
 *  - **No identifier is echoed.** Nothing from the request is interpolated into
 *    the message — no monitor id, no organisation id, no URL fragment. If the
 *    API would not confirm a thing exists, neither does this.
 *
 * A failure that is not a problem document at all (an HTML error page from a
 * proxy, a network drop) is rendered as itself rather than dressed up as one —
 * `HalyardMalformedResponseError` and a bare transport failure say different
 * things and a reader needs to know which they are looking at.
 */
import {
  HalyardConfigurationError,
  HalyardMalformedResponseError,
  HalyardProblemError,
  uiSafeMessage,
} from "../api/index.ts";
import type { ProblemDocument, SafeMessageOptions } from "../api/index.ts";

export interface ProblemNoticeProps {
  readonly error: unknown;
  /** Set on the sign-in screen, where a 401 means "that pair did not match". */
  readonly options?: SafeMessageOptions;
  /** Rendered under the notice — a retry button, usually. */
  readonly children?: React.ReactNode;
}

function ProblemFields({ problem }: { readonly problem: ProblemDocument }): React.JSX.Element {
  return (
    <dl className="problem-fields">
      <div>
        <dt>type</dt>
        <dd>
          <code>{problem.type}</code>
        </dd>
      </div>
      <div>
        <dt>title</dt>
        <dd>{problem.title}</dd>
      </div>
      <div>
        <dt>status</dt>
        <dd>{problem.status}</dd>
      </div>
      {problem.detail === undefined ? null : (
        <div>
          <dt>detail</dt>
          <dd>{problem.detail}</dd>
        </div>
      )}
      <div>
        <dt>request_id</dt>
        <dd>
          <code>{problem.request_id}</code>
        </dd>
      </div>
    </dl>
  );
}

export function ProblemNotice({ error, options, children }: ProblemNoticeProps): React.JSX.Element {
  // The base URL was never configured. This is a deployment fault, not an API
  // failure, and saying so is more useful than any status code would be.
  if (error instanceof HalyardConfigurationError) {
    return (
      <div className="notice notice--error" role="alert">
        <p className="notice__headline">This deployment is not configured to reach the API.</p>
        <p className="notice__body">{error.message}</p>
        {children}
      </div>
    );
  }

  if (error instanceof HalyardProblemError) {
    const safe = uiSafeMessage(error, options ?? {});
    return (
      <div className="notice notice--error" role="alert">
        <p className="notice__headline">{safe.text}</p>
        <p className="notice__caption">What the API returned (RFC 9457 problem details):</p>
        <ProblemFields problem={error.problem} />
        {children}
      </div>
    );
  }

  if (error instanceof HalyardMalformedResponseError) {
    return (
      <div className="notice notice--error" role="alert">
        <p className="notice__headline">The API returned something this client could not read.</p>
        <p className="notice__body">{error.message}</p>
        {error.requestId === null ? null : (
          <p className="notice__body">
            request_id: <code>{error.requestId}</code>
          </p>
        )}
        {children}
      </div>
    );
  }

  // No response at all — DNS, TLS, an offline tab, a proxy that dropped the
  // connection. There is no problem document to show and inventing fields for
  // one would be a lie about where the failure was.
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__headline">The request could not be completed.</p>
      <p className="notice__body">
        No response was received from the API, so there is no problem document to show. This is a
        network or deployment failure rather than something the API reported.
      </p>
      {children}
    </div>
  );
}
